import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLinkedin, FaGithub, FaPaperPlane, FaRegQuestionCircle } from 'react-icons/fa';
import { FiArrowUpRight } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const channels = [
    {
        icon: FaEnvelope,
        label: 'Email',
        value: 'taimanizar45@gmail.com',
        href: 'mailto:taimanizar45@gmail.com',
    },
    {
        icon: FaLinkedin,
        label: 'LinkedIn',
        value: 'linkedin.com/in/taimahamadneh',
        href: 'https://www.linkedin.com/in/taimahamadneh/',
    },
    {
        icon: FaGithub,
        label: 'GitHub',
        value: 'github.com/TaimaHamadneh',
        href: 'https://github.com/TaimaHamadneh',
    },
];

const fieldClass =
    'w-full rounded-2xl border border-white/10 bg-white/[0.06] px-6 py-5 text-white placeholder:text-white/60 outline-none transition focus:border-pink-300/60 focus:bg-white/[0.09]';

/** Thin "circuit" lines in the corners, like the reference design. */
const CircuitLine = ({ className = '', flip = false }) => (
    <svg
        viewBox="0 0 320 60"
        className={className}
        aria-hidden="true"
        style={flip ? { transform: 'scaleX(-1)' } : undefined}
    >
        <path d="M0 4 C 20 4, 30 40, 60 40 L 286 40" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
        <circle cx="294" cy="40" r="8" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
    </svg>
);

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('https://formspree.io/f/xzdbobke', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success("Message sent successfully! I'll get back to you soon.", {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "colored"
                });
                setFormData({ name: '', email: '', message: '' });
            } else {
                throw new Error('Form submission failed');
            }
        } catch (error) {
            toast.error('Something went wrong. Please try again later.', {
                position: "top-right",
                autoClose: 5000,
                theme: "colored"
            });
        }
    };

    return (
        <section id="contact" className="bg-cream px-3 py-20 md:px-6">
            <ToastContainer />
            <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-ink text-white">
                {/* Background: dark teal with a soft glow behind the watermark */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            'radial-gradient(60% 55% at 40% 0%, rgba(166,95,59,0.38) 0%, transparent 70%), radial-gradient(40% 50% at 100% 100%, rgba(105,112,90,0.22) 0%, transparent 70%), linear-gradient(180deg, #332e27 0%, #1d1a16 100%)',
                    }}
                />

                {/* Giant faded watermark */}
                <p
                    aria-hidden="true"
                    className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[18%] select-none whitespace-nowrap font-heading text-[18vw] font-extrabold uppercase leading-none tracking-tight text-transparent md:text-[11rem] lg:text-[13rem]"
                    style={{
                        backgroundImage: 'linear-gradient(180deg, rgba(243,237,227,0.22), rgba(243,237,227,0) 85%)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                    }}
                >
                    Contact
                </p>

                <CircuitLine className="pointer-events-none absolute left-0 top-6 hidden w-64 md:block" />
                <CircuitLine flip className="pointer-events-none absolute right-0 top-10 hidden w-64 md:block" />

                <div className="relative grid grid-cols-1 gap-8 px-5 pb-8 pt-16 md:px-10 md:pb-10 md:pt-24 lg:grid-cols-2 lg:gap-6">
                    {/* Left column */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="flex flex-col"
                    >
                        <span className="inline-flex w-fit items-center gap-3 rounded-full border border-white/15 bg-white/[0.06] py-1.5 pl-1.5 pr-5 text-sm">
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10">
                                <FaRegQuestionCircle aria-hidden="true" />
                            </span>
                            Contact
                        </span>

                        <h2 className="mt-8 font-heading text-4xl font-semibold md:text-5xl">Get In Touch</h2>
                        <p className="mt-4 max-w-md text-lg text-white/75">
                            Have a project to build, a team to lead, or a partnership to discuss? Feel free to reach out!
                        </p>

                        <ul className="mt-10 space-y-4 lg:mt-auto lg:pt-12">
                            {channels.map(({ icon: Icon, label, value, href }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition hover:-translate-y-1 hover:border-pink-300/40 hover:bg-white/[0.08] md:p-5"
                                    >
                                        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-white/15 bg-white/[0.04] text-2xl text-pink-200">
                                            <Icon aria-hidden="true" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-lg font-semibold">{label}</span>
                                            <span className="mt-0.5 block truncate text-white/75">{value}</span>
                                        </span>
                                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-lg transition group-hover:bg-pink-500 group-hover:text-white">
                                            <FiArrowUpRight aria-hidden="true" />
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Right column: form */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                        className="rounded-[24px] border border-white/10 bg-black/20 p-3 backdrop-blur-sm md:p-4"
                    >
                        <form onSubmit={handleSubmit} className="flex h-full flex-col gap-4">
                            <label className="sr-only" htmlFor="contact-name">Your name</label>
                            <input
                                id="contact-name"
                                type="text"
                                name="name"
                                placeholder="Your name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className={fieldClass}
                            />

                            <label className="sr-only" htmlFor="contact-email">Email</label>
                            <input
                                id="contact-email"
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className={fieldClass}
                            />

                            <label className="sr-only" htmlFor="contact-message">Message</label>
                            <textarea
                                id="contact-message"
                                name="message"
                                rows="8"
                                placeholder="Your message here..."
                                required
                                value={formData.message}
                                onChange={handleChange}
                                className={`${fieldClass} min-h-[220px] flex-1 resize-none`}
                            />

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white py-5 text-lg font-semibold text-ink transition hover:bg-cream"
                            >
                                <FaPaperPlane aria-hidden="true" />
                                Send Message
                            </motion.button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
